import React, { useState, useEffect } from 'react';
import { all_routes } from '../../../router/all_routes';
import { Link, useNavigate } from 'react-router-dom';
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from '../../../../core/common/collapse-header/collapse-header';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders } from '../../../../common/Utils'; 
import PreLoader from '../../../../core/common/preLoader';
import CopyRightInfo from '../../../../core/common/copyRightInfo';
import { useAccess } from '../../../../hooks/accessContext';
import CustomToast from '../../../../core/common/customToast';
import CustomModal from '../../../../core/common/customModal';
import UserSubscriptionDetails from './userSubscriptionDetails';
import UserEditModal from './userEditModal';
import AddSubscriptionModal from './addSubscriptionModal';
import "../../../../style/css/shared.scss";
import { UserSubscription } from '../../../../types/dataModel';



const UserSubscriptions = () => {
  const { hasAcademicMgntCreate, hasAcademicMgntDelete, hasAcademicMgntEdit } = useAccess();
  const { isLoading, error, callFetch } = useFetch();
  const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAddSubscriptionModal, setShowAddSubscriptionModal] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [roleOpts, setRoleOpts] = useState<any[]>([]);
  const [editUserRoles, setEditUserRoles] = useState<any[]>([]);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<'table' | 'card'>('table');

  useEffect(() => {
    fetchUserSubscriptions();
    fetchRoles();
  }, []);

  const fetchRoles = () => {
    callFetch(
      `${API.ROLES}?includePermissions=false`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res: any[]) => {
      if (res) {
        let opts: any[] = [];
        res.forEach((item) => {
          opts.push({
            label: item.name,
            value: item.id,
          });
        });
        setRoleOpts(opts);
      }
    });
  };

  const fetchUserRoles = (id: number) => {
    setEditUserRoles([]);
    callFetch(`${API.USER_ACCT}/${id}/roles`, 'GET', getAPIHeaders(), null, null).then(
      (res: any[]) => {
        if (res) {
          let opts: any[] = [];
          res.forEach((item) => {
            opts.push({
              label: item.name,
              value: item.id,
            });
          });
          setEditUserRoles(opts);
        }
      }
    );
  };


  useEffect(() => {
    if(error){
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: error?.response?.data || "Something went wrong please try again later",
        heading: "Error",
        type: "ERROR",
      }));
    }
  }, [error]);

  const fetchUserSubscriptions = () => {
    callFetch(
      `${API.SUBSCRIPTION_PLAN}/users`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      setUserSubscriptions(response || []);
      if(selectedUser){
        setSelectedUser(response.find((user: any) => user.userAccountId === selectedUser.userAccountId));
      }
    }).catch((err) => {
    });
  };

  const handleShowSubscriptions = (user: any) => {
    setSelectedUser(user);
    setShowSubscriptionModal(true);
  };

  const handleEditUser = (user: any) => {
    setEditUser(user);
    if (user?.userAccountId) {
      fetchUserRoles(user.userAccountId);
    }
  };

  const handleUserEditSuccess = () => {
    fetchUserSubscriptions();
    setEditUser(null);
  };

  const handleAddSubscription = (user: any) => {
    setSelectedUser(user);
    setShowAddSubscriptionModal(true);
  };

  const handleSubscriptionStatusToggle = (userAccountId: number, subscriptionEnrollmentId: number) => {
  };

  const getSubscriptionCounts = (subscriptions: any[]) => {
    const total = subscriptions.length;
    const active = subscriptions.filter(sub => sub.planIsActive).length;
    const inactive = total - active;
    return { total, active, inactive };
  };

  const hasAdminRole = (userRoles?: string): boolean => {
    if (!userRoles) return false;
    const roles = userRoles.split(',').map(role => role.trim().toUpperCase());
    return roles.includes('ADMIN');
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "userAccountId",
      render: (text: String, record: any) => (
        <h6 className="fw-medium text-center">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a.userAccountId - b.userAccountId,
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "User Details",
      dataIndex: "userFullName",
      render: (text: String, record: any) => {
        const isTester = hasAdminRole(record.userRoles);
        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-sm me-2 flex-shrink-0 bg-light-primary rounded-circle">
              <span className="text-primary fw-bold">
                {text.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1">
                <h6 className="fw-medium mb-0">
                  <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => handleEditUser(record)}
                    style={{ color: 'inherit', fontWeight: 'inherit' }}
                  >
                    {text}
                  </button>
                </h6>
                {isTester && (
                  <span className="badge bg-warning text-dark">
                    TESTER
                  </span>
                )}
              </div>
              <p className="text-muted mb-0 small">@{record.username}</p>
            </div>
          </div>
        );
      },
      sorter: (a: any, b: any) => a.userFullName.localeCompare(b.userFullName),
      width: "20%",
    },
    {
      title: "Contact Info",
      dataIndex: "email",
      render: (text: string, record: any) => (
        <div>
          <div className="mb-1">
            <i className="ti ti-mail me-1 text-muted"></i>
            <span className="small">{text}</span>
          </div>
          <div>
            <i className="ti ti-phone me-1 text-muted"></i>
            <span className="small">{record.phoneNumber}</span>
          </div>
        </div>
      ),
      width: "20%",
    },
    {
      title: "Profile Status",
      dataIndex: "profileStatus",
      render: (text: string, record: any) => {
        const getBadgeClass = (status: string) => {
          switch (status) {
            case 'ACTIVE':
              return 'bg-success';
            case 'INACTIVE':
              return 'bg-danger';
            case 'PENDING':
              return 'bg-warning';
            default:
              return 'bg-secondary';
          }
        };
        return (
          <span className={`badge ${getBadgeClass(text)}`}>
            {text}
          </span>
        );
      },
      width: "12%",
      align: "center" as "center",
    },
    {
      title: "Subscriptions",
      dataIndex: "subscriptions",
      render: (subscriptions: any[], record: any) => {
        const counts = getSubscriptionCounts(subscriptions);
        return (
          <div className="text-center">
            <div className="mb-1">
              <span className="badge bg-primary me-1">Total: {counts.total}</span>
            </div>
            <div>
              <span className="badge bg-success me-1">Active: {counts.active}</span>
              <span className="badge bg-secondary">Inactive: {counts.inactive}</span>
            </div>
          </div>
        );
      },
      width: "20%",
      align: "center" as "center",
    },
    {
      title: "Actions",
      dataIndex: "userAccountId",
      render: (text: number, record: any) => (
        <div className="d-flex justify-content-center gap-2">      
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleEditUser(record)}
            title="Edit User"
          >
            <i className="ti ti-edit" />
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleShowSubscriptions(record)}
            title="View Subscriptions"
          >
            <i className="ti ti-eye me-1" />
            View Subscriptions
          </button>
        </div>
      ),
      width: "20%",
      align: "center" as "center",
    },
  ];

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">User Subscriptions</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Online Learning Center</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    User Subscriptions
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>User Subscription List</h5>
              <div className="view-toggle">
                <button 
                  className={`btn btn-sm me-2 ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('table')}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1"></i> Table View
                </button>
                <button 
                  className={`btn btn-sm ${viewType === 'card' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('card')}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1"></i> Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === 'table' ? (
                <Table
                  dataSource={userSubscriptions}
                  columns={columns}
                  Selection={false}
                  refresh={fetchUserSubscriptions}
                  onRow={(record) => ({
                    style: hasAdminRole(record.userRoles) 
                      ? { backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' } 
                      : {},
                  })}
                />
              ) : (
                <div className="user-subscription-cards p-3">
                  <div className="row">
                    {userSubscriptions.map((user: any) => {
                      const counts = getSubscriptionCounts(user.subscriptions);
                      const isAdmin = hasAdminRole(user.userRoles);
                      return (
                        <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={user.userAccountId}>
                          <div 
                            className="card h-100 border shadow-hover transition-all"
                            style={isAdmin ? { 
                              backgroundColor: '#fff3cd', 
                              borderLeft: '4px solid #ffc107',
                              boxShadow: '0 0 0 1px rgba(255, 193, 7, 0.2), 0 2px 8px rgba(255, 193, 7, 0.15)'
                            } : {}}
                          >
                            <div className="card-body position-relative">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="d-flex align-items-center flex-grow-1">
                                  <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                    <span className="text-primary fw-bold">
                                      {user.userFullName.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <h6 className="fw-medium mb-0">
                                        <button
                                          className="btn btn-link p-0 text-decoration-none"
                                          onClick={() => handleEditUser(user)}
                                          style={{ color: 'inherit', fontWeight: 'inherit' }}
                                        >
                                          {user.userFullName}
                                        </button>
                                      </h6>
                                      {isAdmin && (
                                        <span className="badge bg-warning text-dark tester-badge">
                                          TESTER
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-muted mb-0 small">@{user.username}</p>
                                  </div>
                                </div>
                                <div>
                                  <span className={`badge ${user.profileStatus === 'ACTIVE' ? 'bg-success' : 'bg-danger'}`}>
                                    {user.profileStatus}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="mb-3">
                                <div className="mb-2">
                                  <i className="ti ti-mail me-1 text-muted"></i>
                                  <span className="small">{user.email}</span>
                                </div>
                                <div>
                                  <i className="ti ti-phone me-1 text-muted"></i>
                                  <span className="small">{user.phoneNumber}</span>
                                </div>
                              </div>

                              <div className="mb-3">
                                <h6 className="mb-2">Subscriptions</h6>
                                <div className="d-flex gap-2 flex-wrap">
                                  <span className="badge bg-primary">Total: {counts.total}</span>
                                  <span className="badge bg-success">Active: {counts.active}</span>
                                  <span className="badge bg-secondary">Inactive: {counts.inactive}</span>
                                </div>
                              </div>
                              
                              <div className="d-flex justify-content-center gap-2 mt-3">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleShowSubscriptions(user)}
                                  title="View Subscriptions"
                                >
                                  <i className="ti ti-eye me-1" />
                                  View Subscriptions
                                </button>
                                {hasAcademicMgntCreate && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleAddSubscription(user)}
                                    title="Add Subscription"
                                  >
                                    <i className="ti ti-plus me-1" />
                                    Add Subscription
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {userSubscriptions.length === 0 && (
                      <div className="col-12 text-center py-5">
                        <div className="empty-state">
                          <i className="ti ti-users-off fs-1 text-muted mb-3"></i>
                          <h5 className="mb-2">No user subscriptions found</h5>
                          <p className="text-muted">No users with subscriptions match your current filter criteria.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /User subscription list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* User Subscription Details Modal */}
      {selectedUser && (
        <CustomModal
          show={showSubscriptionModal}
          title={`${selectedUser.userFullName}'s Subscriptions`}
          onClose={() => {
            setShowSubscriptionModal(false);
            setSelectedUser(null);
          }}
          size="xl"
          headerContent={
            <div className="ms-2">
              {hasAcademicMgntCreate && <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowAddSubscriptionModal(true)}
                title="Add Subscription"
              >
                <i className="ti ti-plus me-1" />
                Add Subscription
              </button>}
            </div>
          }
          headerClassName="d-flex justify-content-between w-100 me-4 align-items-center"
          bodyContent={
            <UserSubscriptionDetails
              isReadOnly={!(hasAcademicMgntCreate || hasAcademicMgntEdit)}
              user={selectedUser}
              onStatusToggle={handleSubscriptionStatusToggle}
              onRefresh={fetchUserSubscriptions}
            />
          }
        />
      )}

      {/* User Edit Modal */}
      <UserEditModal
        user={editUser}
        onSuccess={handleUserEditSuccess}
        onClose={() => {
          setEditUser(null);
          setEditUserRoles([]);
        }}
        roleOpts={roleOpts}
        userRoles={editUserRoles}
      />

      {/* Add Subscription Modal */}
      {selectedUser && showAddSubscriptionModal && (
        <AddSubscriptionModal
          user={selectedUser}
          onSuccess={fetchUserSubscriptions}
          onClose={() => {
            setShowAddSubscriptionModal(false);
          }}
        />
      )}
    </>
  );
};

export default UserSubscriptions;
